import {Stack, ThemeProvider} from '@primer/react-brand'

import {
  ContentfulAnchorNav,
  ContentfulCards,
  ContentfulCtaBanner,
  ContentfulFaq,
  ContentfulHero,
  ContentfulPillars,
  ContentfulRiver,
  ContentfulRiverBreakout,
  ContentfulSectionIntro,
  ContentfulTestimonials,
  ContentfulTimeline,
  type ContentfulCardsProps,
} from '../../components/contentful'
import type {FreeFormPage, TemplateFreeFormSection} from '../../lib/types/contentful'
import type {ArrayElement} from '../../lib/types/utils'
import {SectionBackgroundImage} from './components/SectionBackgroundImage'

type FreeFormSectionComponents = ArrayElement<TemplateFreeFormSection['fields']['components']>

function getReactComponentFor<T extends FreeFormSectionComponents>(component: T): React.ElementType {
  const map: Record<FreeFormSectionComponents['sys']['contentType']['sys']['id'], React.ElementType> = {
    primerComponentAnchorNav: ContentfulAnchorNav,
    primerComponentHero: ContentfulHero,
    primerComponentSectionIntro: ContentfulSectionIntro,
    primerComponentRiver: ContentfulRiver,
    primerComponentRiverBreakout: ContentfulRiverBreakout,
    primerCards: (props: Required<Pick<ContentfulCardsProps, 'component'>>) => (
      <ContentfulCards {...props} className="FreeForm__Cards" />
    ),
    primerComponentFaq: ContentfulFaq,
    primerComponentCtaBanner: ContentfulCtaBanner,
    primerPillars: ContentfulPillars,
    primerTestimonials: ContentfulTestimonials,
    primerComponentTimeline: ContentfulTimeline,
  }

  return map[component.sys.contentType.sys.id]
}

export type FreeFormTemplateProps = {
  page: FreeFormPage
}

export function FreeFormTemplate({page}: FreeFormTemplateProps) {
  const pageColorMode = page.fields.settings?.fields.colorMode ?? 'light'

  return (
    <ThemeProvider colorMode={pageColorMode} style={{backgroundColor: 'var(--brand-color-canvas-default)'}}>
      <Stack
        padding="none"
        gap="spacious"
        direction="vertical"
        className="MktLandingPage pb-8"
        style={{minHeight: '100vh'}}
      >
        {page.fields.template.fields.sections.map(section => {
          const sectionColorMode = section.fields.colorMode === 'inherit' ? pageColorMode : section.fields.colorMode

          return (
            <ThemeProvider
              key={section.sys.id}
              colorMode={sectionColorMode}
              style={{backgroundColor: 'var(--brand-color-canvas-default)'}}
            >
              <section className="width-full position-relative" id={section.fields.id}>
                <SectionBackgroundImage fields={section.fields} />
                {section.fields.components.map(component => {
                  const ContentfulComponent = getReactComponentFor(component)

                  return (
                    <div key={component.sys.id} className="container-xl position-relative p-responsive">
                      <ContentfulComponent component={component} />
                    </div>
                  )
                })}
              </section>
            </ThemeProvider>
          )
        })}
      </Stack>
    </ThemeProvider>
  )
}

try{ FreeFormTemplate.displayName ||= 'FreeFormTemplate' } catch {}
try{ ContentfulComponent.displayName ||= 'ContentfulComponent' } catch {}